import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eraser, Signature as SigIcon, User, HardHat } from "@phosphor-icons/react";
import { enqueue, isOnline } from "@/lib/offlineQueue";

export default function SignaturePad({ open, onClose, chantierId, interventionId, defaultClientName = "", lockSignerType, hideTypeToggle = false, onSaved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [clientName, setClientName] = useState(defaultClientName);
  const [signerType, setSignerType] = useState(lockSignerType || "client");
  const [empty, setEmpty] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => setClientName(defaultClientName), [defaultClientName, open]);
  useEffect(() => { if (lockSignerType) setSignerType(lockSignerType); }, [lockSignerType, open]);

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setEmpty(true);
  }, [open, signerType]);

  const point = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = point(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setEmpty(false);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setEmpty(true);
  };

  const save = async () => {
    if (empty) return toast.error("Signature vide");
    if (!clientName.trim()) return toast.error("Nom requis");
    setSaving(true);
    try {
      const data_url = canvasRef.current.toDataURL("image/png");
      const payload = { chantier_id: chantierId, intervention_id: interventionId, client_name: clientName, data_url, signer_type: signerType };
      if (!isOnline()) {
        enqueue({ method: "post", url: "/signatures", data: payload });
        toast.info("Signature enregistrée hors ligne");
        onSaved?.();
        return;
      }
      try {
        await api.post("/signatures", payload);
        toast.success("Signature enregistrée");
      } catch {
        enqueue({ method: "post", url: "/signatures", data: payload });
        toast.info("Signature mise en file d'attente");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><SigIcon size={20} /> Signature</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Signer type selector — hidden when the caller pins it (e.g. closure flow) */}
          {!hideTypeToggle && (
            <div className="grid grid-cols-2 gap-2" data-testid="signer-type-toggle">
            <button
              onClick={() => setSignerType("client")}
              className={`rounded-xl border p-3 flex flex-col items-start gap-1 transition-colors ${
                signerType === "client" ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/70"
              }`}
              data-testid="signer-client-btn"
            >
              <User size={16} weight="duotone" />
              <span className="text-xs font-semibold">Client</span>
            </button>
            <button
              onClick={() => setSignerType("intervenant")}
              className={`rounded-xl border p-3 flex flex-col items-start gap-1 transition-colors ${
                signerType === "intervenant" ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white/5 border-white/10 text-white/70"
              }`}
              data-testid="signer-tech-btn"
            >
              <HardHat size={16} weight="duotone" />
              <span className="text-xs font-semibold">Intervenant BKPRO</span>
            </button>
          </div>
          )}

          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">
              {signerType === "client" ? "Nom du client" : "Nom de l'intervenant"}
            </Label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="bg-white/5 border-white/10 h-11 mt-1" data-testid="sig-client-name" />
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white">
            <canvas
              ref={canvasRef}
              className="sig-canvas w-full h-56 block bg-white"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="signature-canvas"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={clear} className="border border-white/10 flex-1"><Eraser size={16} /> Effacer</Button>
            <Button onClick={save} disabled={saving} className={`flex-1 ${signerType === "client" ? "bg-[#005CFF] hover:bg-[#0047CC]" : "bg-emerald-500 hover:bg-emerald-600"}`} data-testid="sig-save">
              {saving ? "…" : "Valider"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
